import { type ReactNode, type SyntheticEvent, useState } from 'react';

import { z } from 'zod';

import { appConfig } from '@/shared/config/app-config';

import { signUpWithEmail } from '../api/sign-up-with-email';
import { signUpWithEmailSchema, validationCopy } from '../model/sign-up-with-email.schema';
import { EmailInUseError } from '../ui/sign-up-with-email/sign-up-with-email-email-in-use-error';

const genericErrorCopy = 'Something went wrong. Please try again.';

type FieldName = 'email' | 'password';

type FieldErrors = {
    email?: ReactNode;
    password?: string;
    form?: string;
};

const fieldOrder: FieldName[] = ['email', 'password'];

export function useSignUpWithEmail(signUpData?: string) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateField = (field: FieldName, value: string) => {
        const result = signUpWithEmailSchema.shape[field].safeParse(value);
        setErrors((prev) => ({
            ...prev,
            [field]: result.success ? undefined : result.error.issues[0]?.message,
        }));
    };

    const handleSubmit = async (
        event: SyntheticEvent<HTMLFormElement>,
    ): Promise<FieldName | undefined> => {
        event.preventDefault();
        if (isSubmitting) return;

        const parsed = signUpWithEmailSchema.safeParse({ email, password });
        if (!parsed.success) {
            const flattened = z.flattenError(parsed.error).fieldErrors;
            setErrors({
                email: flattened.email?.[0],
                password: flattened.password?.[0],
            });
            return fieldOrder.find((field) => flattened[field]?.[0]);
        }

        setErrors({});
        setIsSubmitting(true);

        const result = await signUpWithEmail({ ...parsed.data, signUpData });

        if (result.ok) {
            // The API returns the finalize URL (already carries ?finalizing=true); just follow it.
            const fallback = new URL('/dashboard', appConfig.notesAppUrl).toString();
            window.location.href = result.returnUrl ?? fallback;
            return;
        }

        setIsSubmitting(false);
        switch (result.code) {
            case 'EMAIL_IN_USE':
                setErrors({ email: <EmailInUseError /> });
                return 'email';
            case 'WEAK_PASSWORD':
                setErrors({ password: validationCopy.passwordComplexity });
                return 'password';
            default:
                setErrors({ form: genericErrorCopy });
                return undefined;
        }
    };

    return {
        email,
        password,
        errors,
        isSubmitting,
        setEmail: (value: string) => {
            setEmail(value);
            if (errors.email || errors.form) {
                setErrors((prev) => ({ ...prev, email: undefined, form: undefined }));
            }
        },
        setPassword: (value: string) => {
            setPassword(value);
            if (errors.password || errors.form) {
                setErrors((prev) => ({ ...prev, password: undefined, form: undefined }));
            }
        },
        handleEmailBlur: () => validateField('email', email),
        handlePasswordBlur: () => validateField('password', password),
        handleSubmit,
    };
}
